"use client";

import { useState, useEffect } from "react";
import { usePathname } from "next/navigation";
import { motion, AnimatePresence } from "framer-motion";
import Link from "next/link";
import { Logo } from "@/app/_components/icons";

const testimonials = [
  {
    quote: "From zero to live in 3 weeks. They delivered beyond what we expected.",
    highlight: "3 weeks",
    author: "SaaS Founder, London",
  },
  {
    quote: "They didn't just build our site. They built the system behind our growth.",
    highlight: "growth",
    author: "E-commerce Director, New York",
  },
  {
    quote: "Fast, reliable, and they actually understand business. Rare combo.",
    highlight: "Rare combo",
    author: "Startup CEO, Dubai",
  },
  {
    quote: "Our revenue doubled in 6 months after the rebuild.",
    highlight: "doubled",
    author: "Restaurant Chain, Toronto",
  },
];

const benefits = [
  { text: "Build websites and apps that make you money.", highlight: "money" },
  { text: "Fast delivery. Real engineering. Zero excuses.", highlight: "Zero excuses" },
  { text: "Trusted by 100+ businesses in the US, UK, Canada & UAE.", highlight: "100+ businesses" },
  { text: "Your conversation starts the moment you sign up.", highlight: "sign up" },
];

/** Renders `text` in primary, with the `highlight` substring in secondary — same font, no italic. */
function HighlightedText({ text, highlight }: { text: string; highlight: string }) {
  const idx = text.indexOf(highlight);
  if (idx === -1) return <>{text}</>;
  return (
    <>
      {text.slice(0, idx)}
      <span className="text-secondary">{highlight}</span>
      {text.slice(idx + highlight.length)}
    </>
  );
}

function Stars() {
  return (
    <div className="flex items-center gap-1.5">
      {[...Array(5)].map((_, i) => (
        <svg key={i} className="w-5 h-5 fill-black" viewBox="0 0 20 20">
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      ))}
      <span className="text-sm font-medium ml-1 text-black/70">Trusted by 100+ businesses</span>
    </div>
  );
}

function NavDots({ total, current, onChange }: { total: number; current: number; onChange: (i: number) => void }) {
  return (
    <div className="flex gap-2">
      {Array.from({ length: total }).map((_, i) => (
        <button
          key={i}
          onClick={() => onChange(i)}
          className={`h-1.5 rounded-full transition-all duration-300 ${
            i === current ? "w-8 bg-black" : "w-2.5 bg-black/25"
          }`}
        />
      ))}
    </div>
  );
}

export default function AuthCarouselPanel() {
  const pathname = usePathname();
  const [current, setCurrent] = useState(0);

  const isSignup = pathname.startsWith("/signup");
  const isSetup = pathname.startsWith("/complete-setup");
  const isForgot = pathname.startsWith("/forgot-password");
  const isLogin = !isSignup && !isSetup && !isForgot;

  const items = isSignup ? benefits : testimonials;

  useEffect(() => {
    const id = setInterval(() => setCurrent((c) => (c + 1) % items.length), 4500);
    return () => clearInterval(id);
  }, [items.length]);

  return (
    <div className="hidden lg:flex flex-col justify-between bg-[#77b441]/10 p-14 text-black min-h-screen w-full">
      {/* Logo — click goes home */}
      <Link href="/" className="text-[36px] hover:opacity-80 transition-opacity">
        <Logo />
      </Link>

      {/* Middle content */}
      {isSetup ? (
        <div className="flex flex-col gap-6">
          <div className="w-16 h-16 rounded-full bg-primary/15 flex items-center justify-center">
            <svg className="w-8 h-8 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
                d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
              />
            </svg>
          </div>
          <h2 className="text-[42px] font-semibold leading-tight">Almost there</h2>
          <p className="text-black/60 text-[20px] leading-relaxed max-w-[340px]">
            Set a password to secure your account and pick up your conversation right where you left off.
          </p>
        </div>
      ) : isForgot ? (
        <div className="flex flex-col gap-6">
          <div className="w-16 h-16 rounded-full bg-primary/15 flex items-center justify-center">
            <svg className="w-8 h-8 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
                d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
              />
            </svg>
          </div>
          <h2 className="text-[42px] font-semibold leading-tight">Reset your password</h2>
          <p className="text-black/60 text-[20px] leading-relaxed max-w-[340px]">
            We&apos;ll send a verification code to your email. Use it to set a new password in seconds.
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-7">
          {isLogin && <Stars />}

          {isSignup && (
            <p className="text-[13px] font-semibold uppercase tracking-[0.18em] text-black/40">
              Why businesses choose us
            </p>
          )}

          <div className="min-h-[160px]">
            <AnimatePresence mode="wait">
              <motion.div
                key={current}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.35 }}
              >
                {isLogin ? (
                  <>
                    <p className="text-[38px] lg:text-[44px] font-medium leading-tight mb-5 text-primary">
                      &ldquo;
                      <HighlightedText
                        text={testimonials[current].quote}
                        highlight={testimonials[current].highlight}
                      />
                      &rdquo;
                    </p>
                    <p className="text-[16px] font-serif text-black/55">{testimonials[current].author}</p>
                  </>
                ) : (
                  <p className="text-[38px] lg:text-[44px] font-medium leading-tight text-primary">
                    <HighlightedText
                      text={benefits[current].text}
                      highlight={benefits[current].highlight}
                    />
                  </p>
                )}
              </motion.div>
            </AnimatePresence>
          </div>

          <NavDots total={items.length} current={current} onChange={setCurrent} />
        </div>
      )}

      {/* Bottom spacer for visual balance */}
      <div />
    </div>
  );
}
